import { useState, useEffect, useCallback, useRef } from "react";
import { getPostViewTracker } from "../helpers/PostViewTracker";

/**
 * Hook for post view tracking
 *
 * Provides:
 * - viewCounts: Map of postId -> viewCount
 * - updateViewCount: Function to update view count
 * - startTracking: Function to start tracking a post
 * - stopTracking: Function to stop tracking a post
 *
 * @param {Function} apiCall - Function to call batch views API
 * @returns {Object} Tracking utilities
 */
export const usePostViewTracking = (apiCall) => {
  const [viewCounts, setViewCounts] = useState(new Map());
  const trackerRef = useRef(null);
  const apiCallRef = useRef(apiCall);

  // Update API call ref when it changes
  useEffect(() => {
    apiCallRef.current = apiCall;
  }, [apiCall]);

  // Initialize tracker
  useEffect(() => {
    const tracker = getPostViewTracker();
    trackerRef.current = tracker;

    // Set up view count update callback
    tracker.setViewCountUpdateCallback((postId, count) => {
      setViewCounts((prev) => {
        const newMap = new Map(prev);
        newMap.set(postId, count);
        return newMap;
      });
    });

    // Set up periodic flush with API call
    const flushInterval = setInterval(async () => {
      if (tracker.getBatchQueue().length > 0 && apiCallRef.current) {
        await tracker.flushBatch(apiCallRef.current);
      }
    }, 15000); // 15 seconds

    // Cleanup on unmount
    return () => {
      clearInterval(flushInterval);
      // Flush remaining views before cleanup
      if (apiCallRef.current) {
        tracker.flushRemaining(apiCallRef.current);
      }
    };
  }, []);

  /**
   * Start tracking a post
   * @param {number} postId - Post ID
   * @param {HTMLElement} element - Post DOM element
   * @param {number} initialViewCount - Initial view count from API
   * @param {boolean} isViewedByCurrentUser - Whether post is already viewed by current user
   */
  const startTracking = useCallback(
    (postId, element, initialViewCount = 0, isViewedByCurrentUser = false) => {
      if (!trackerRef.current || !element) return;

      // Update initial view count
      if (initialViewCount !== undefined && initialViewCount !== null) {
        setViewCounts((prev) => {
          const newMap = new Map(prev);
          newMap.set(postId, initialViewCount);
          return newMap;
        });
      }

      trackerRef.current.startTracking(
        postId,
        element,
        initialViewCount,
        isViewedByCurrentUser,
      );
    },
    [],
  );

  /**
   * Stop tracking a post
   * @param {number} postId - Post ID
   */
  const stopTracking = useCallback((postId) => {
    if (!trackerRef.current) return;
    trackerRef.current.stopTracking(postId);
  }, []);

  /**
   * Update view count (from API response)
   * @param {number} postId - Post ID
   * @param {number} count - View count
   */
  const updateViewCount = useCallback((postId, count) => {
    if (!trackerRef.current) return;
    trackerRef.current.updateViewCount(postId, count);
  }, []);

  /**
   * Get view count for a post
   * @param {number} postId - Post ID
   * @returns {number} View count
   */
  const getViewCount = useCallback((postId) => {
    if (!trackerRef.current) return 0;
    return trackerRef.current.getViewCount(postId);
  }, []);

  /**
   * Manually flush batch (for testing or immediate sync)
   */
  const flushBatch = useCallback(async () => {
    if (!trackerRef.current || !apiCallRef.current) return;
    await trackerRef.current.flushBatch(apiCallRef.current);
  }, []);

  return {
    viewCounts,
    startTracking,
    stopTracking,
    updateViewCount,
    getViewCount,
    flushBatch,
  };
};

export default usePostViewTracking;
